import { createContext, useContext, useState, type ReactNode } from "react";

// Vetted scoring models — must stay in sync with the backend allowlist
// (functions/src/providers/index.ts → SCORING_MODEL_ALLOWLIST).
//
// `enabled` controls whether a model appears in the picker. Flip it here to
// show/hide a model; this is the single seam if we later drive availability
// from an env var or a Firestore config doc (replace the static `enabled`).
export interface ScoringModelOption {
  id: string;
  label: string;
  enabled: boolean;
}

export const SCORING_MODELS: ScoringModelOption[] = [
  { id: "claude-sonnet-4-6", label: "Sonnet 4.6 — default", enabled: true },
  { id: "claude-haiku-4-5", label: "Haiku 4.5 — faster, scores harsher", enabled: true },
  // Opus 4.8 hidden for now — flip `enabled` to re-expose it.
  { id: "claude-opus-4-8", label: "Opus 4.8 — most capable", enabled: false },
];

/** Models shown in the picker. */
export const ENABLED_SCORING_MODELS = SCORING_MODELS.filter((m) => m.enabled);

const DEFAULT_MODEL = ENABLED_SCORING_MODELS[0].id;
const STORAGE_KEY = "og.scoringModel";

interface Ctx {
  model: string;
  setModel: (id: string) => void;
}

const ScoringModelCtx = createContext<Ctx>({
  model: DEFAULT_MODEL,
  setModel: () => undefined,
});

export function ScoringModelProvider({ children }: { children: ReactNode }) {
  const [model, setModelState] = useState<string>(() => {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    // Fall back to default if the saved model is now hidden/disabled.
    return saved && ENABLED_SCORING_MODELS.some((m) => m.id === saved)
      ? saved
      : DEFAULT_MODEL;
  });
  const setModel = (id: string) => {
    setModelState(id);
    window.localStorage.setItem(STORAGE_KEY, id);
  };
  return (
    <ScoringModelCtx.Provider value={{ model, setModel }}>
      {children}
    </ScoringModelCtx.Provider>
  );
}

/** The currently-selected scoring model id (send as `model` on the chat call). */
export function useScoringModel(): string {
  return useContext(ScoringModelCtx).model;
}

/** Full model state ({ model, setModel }) — used where the model is *chosen*
 *  (the New-assessment form), not just read. */
export function useScoringModelState(): Ctx {
  return useContext(ScoringModelCtx);
}

/** Dropdown to choose the scoring model. Grades are only comparable across
 *  canvases scored on the same model — hence the warning in the title. */
export function ScoringModelPicker() {
  const { model, setModel } = useContext(ScoringModelCtx);
  return (
    <label
      className="scoring-model-picker"
      title="Model used to score each answer. Grades are only comparable across canvases scored on the same model."
    >
      <span className="scoring-model-picker__label">Scoring model</span>
      <select
        className="field__input scoring-model-picker__select"
        value={model}
        onChange={(e) => setModel(e.target.value)}
      >
        {ENABLED_SCORING_MODELS.map((m) => (
          <option key={m.id} value={m.id}>
            {m.label}
          </option>
        ))}
      </select>
    </label>
  );
}
